// BUSCADOR DE PACIENTE SIEMPRE A MANO, en la cabecera de toda la app.
//
// POR QUÉ. Para abrir la ficha de un paciente había que ir primero a la página de Pacientes y buscar
// allí. Estando en la agenda, en un chat o en la caja, encontrar a alguien costaba dos saltos. Con
// esto se busca desde cualquier pantalla —por nombre, teléfono, nº de historial, email o NIF— y al
// elegir uno se abre su ficha, sin perder de vista dónde estabas.
//
// Reutiliza lo que ya existe: el endpoint /pacientes?buscar= (mismo que la lista) y la apertura por
// URL /pacientes?paciente=ID que la página de Pacientes ya sabe interpretar (deep-link). No hay
// endpoint nuevo ni lógica de ficha duplicada.
import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import api from '../services/api'
import { usePermisos } from '../hooks/usePermisos'
import { useIsMobile } from '../hooks/useIsMobile'
import FotoPaciente from './FotoPaciente'
import InputBusqueda from './InputBusqueda'
import { Ico } from './ui'
import { t as i18nT, tx } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// EL ALTO DE LA BANDA DE LA CABECERA DE LA APLICACION (`components/Layout.jsx`), de la que este
// buscador cuelga. Al abrirlo en un movil, esta caja se sale de la fila y se pone ENCIMA de esa
// banda: para taparla tiene que medir exactamente lo mismo que ella. Que los dos numeros sigan
// siendo el mismo lo comprueba en pixeles `routes/buscador-app-ancho.test.js`, montando las dos
// pantallas: si alguien cambia el alto de la cabecera y no este, la guardia se pone roja — que es
// lo unico que evita que la capa vuelva a dejar asomar el avatar y los puntos de aviso por debajo.
const ALTO_CABECERA_APP = 58

// CUANTAS FILAS SE TRAEN DE GOLPE en la lista de «hoy». No es un tope: al llegar abajo se pide la
// siguiente tanda (ver `cargarMasHoy`). Treinta llenan de sobra el desplegable mas alto que se
// pinta (360 px dan para unos siete) y dejan margen para bajar sin esperar.
const TANDA_HOY = 30

export default function BuscadorPacienteGlobal() {
  const navigate = useNavigate()
  const { permiso } = usePermisos()
  // B-464 · a su pre-registro solo se manda a quien puede abrir Pre-registros; a los demas, a su cita.
  const puedePre = permiso('pre_registros')
  const [q, setQ] = useState('')
  const [abierto, setAbierto] = useState(false)
  const [idx, setIdx] = useState(0)          // fila resaltada para el teclado
  const boxRef = useRef(null)
  const inputRef = useRef(null)
  // 769 y no el 900 de fabrica, Y AHORA MEDIDO. Este corte decide UNA cosa: a partir de que ancho
  // la caja cerrada ya no puede enseñar ni su propio rotulo y hay que sacarla de la fila y ponerla
  // encima de la cabecera. Midiendo la cabecera montada (`routes/buscador-app-ancho.test.js`), con
  // la cabecera en compacto —los rotulos de los botones escondidos por debajo de 1000 px, ver
  // `Layout.jsx`— al campo le quedan 114 px de sitio para el texto en 769, 179 en un iPad y 245 en
  // 900, y «Buscar paciente…» pide 103: cabe. Por debajo de 769 deja de caber (en un movil de 390 px
  // le quedan -25 con el rotulo corto, que pide 43) y ahi es donde la capa hace falta. Antes este
  // numero se justificaba solo por coincidir con el corte de `.hide-mobile`, y esa coincidencia ya
  // no existe: ahora la manda la medida.
  const isMobile = useIsMobile(769)

  // Sin permiso para ver pacientes, el buscador no se pinta: sería una puerta lateral al listado.
  // La clave es 'clientes' (la del modulo Pacientes). Aqui ponia 'ver_pacientes', que NO existe en el
  // catalogo de permisos — y usePermisos devuelve true para lo que no conoce, asi que el buscador se
  // pintaba hasta para el rol limpieza. Un permiso mal escrito no falla: abre.
  const puede = permiso('clientes') !== false

  // Debounce: no se consulta en cada tecla. 2 caracteres mínimo (con 1 saldría media clínica).
  const [term, setTerm] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setTerm(q.trim()), 250)
    return () => clearTimeout(t)
  }, [q])

  // Antes pedía limit=8: buscabas un apellido corriente y salían 8 aunque hubiera 40, sin forma de
  // saber que faltaban. Ahora trae 25 (un desplegable con scroll los enseña de sobra) y, si hay más,
  // se avisa con un enlace a /pacientes para verlos TODOS. `total` viene del propio endpoint.
  // B-1023 · BUSCA IGUAL QUE LA LISTA DE PACIENTES POR DEFECTO: solo ACTIVOS (`filtro=activo`, el
  // valor de partida de /pacientes) y sin fallecidos (el servidor los quita si no se pide
  // `incluir_exitus`). Antes no mandaba el filtro y aqui salian tambien los dados de baja.
  const { data, isFetching } = useQuery({
    queryKey: ['buscador-global', term],
    queryFn: () => api.get(`/pacientes?page=1&limit=25&buscar=${encodeURIComponent(term)}&filtro=activo`).then(r => r.data),
    enabled: puede && term.length >= 2,
    staleTime: 15000,
  })
  // LA LISTA DEL HUECO VACIO: los pacientes DE HOY. Una sola lista, con la procedencia dentro de
  // cada fila — «Cita hoy» los que vienen hoy, «Vista hoy» las fichas que esta persona ya ha
  // abierto. El servidor las funde: quien esta en las dos sale UNA vez con las dos etiquetas.
  // Solo se piden cuando el desplegable esta abierto y no se ha escrito nada: es lo unico que se
  // enseña en ese hueco, y pedirlas antes seria una consulta por cada carga de pagina que casi
  // nunca se llega a mirar.
  // ── B-332 · LOS DOS BOTONES DE ARRIBA, Y SIN TOPE DE DOCE ───────────────────────────────────
  // Lo pidio el dueño: «que en la parte superior hayan dos botones para separar "Pacientes hoy" y
  // "Visto hoy" para que se organice mejor y tampoco limitar el numero maximo, actualmente limitado
  // a 12». El doce no estaba aqui sino en el servidor (routes/pacientes.js, `/vistas-hoy`), y la
  // pantalla ni se enteraba de que la lista venia cortada.
  //
  // 'todas' es lo de siempre —las dos procedencias fundidas en una lista— y es lo que se ve al
  // abrir. Los dos botones no son un menu de tres opciones: cada uno DEJA SOLO lo suyo, y volver a
  // pulsarlo devuelve la lista entera. Asi el gesto de «quiero ver solo a los que vienen hoy» es un
  // clic, y no hay ningun estado en el que no se vea nada.
  const [fuenteHoy, setFuenteHoy] = useState('todas')
  // Las tandas siguientes a la primera, acumuladas. La primera la sigue trayendo react-query (que
  // es quien la cachea y la relee al abrir); estas se piden a mano segun se baja.
  const [masHoy, setMasHoy] = useState([])
  const [finHoy, setFinHoy] = useState(false)      // ya no queda nada que traer
  const [pidiendoHoy, setPidiendoHoy] = useState(false)
  // EL FRENO DEL SERVIDOR, que la pantalla tiene que poder contar. La lista tiene un tope por
  // procedencia para que un dia disparatado no vuelque miles de filas aqui; cuando se llega a el,
  // el servidor lo dice (`recortado`) y manda el total DE VERDAD. Sin enseñarlo, volveria a pasar lo
  // de siempre: una lista cortada que parece completa, que es lo que esta tarea vino a quitar.
  const [tope, setTope] = useState(null)
  const { data: vistasResp } = useQuery({
    queryKey: ['pacientes-vistas-hoy', fuenteHoy],
    queryFn: () => api.get(`/pacientes/vistas-hoy?fuente=${fuenteHoy}&limite=${TANDA_HOY}&desde=0`)
      .then(r => { setTope(r.data?.recortado ? (r.data.total || null) : null); return r.data?.pacientes || [] })
      .catch(() => []),
    enabled: puede && abierto && term.length < 2,
    // Sin staleTime: al abrir el desplegable se relee. La ficha que se acaba de mirar tiene que
    // estar ahi; con cache de 30 s podia no aparecer justo cuando se la busca.
    staleTime: 0,
  })
  const primeraHoy = vistasResp || []
  const vistas = masHoy.length ? [...primeraHoy, ...masHoy] : primeraHoy

  // Cambiar de boton (o cerrar y volver a abrir) empieza de cero: lo acumulado era de la otra lista.
  useEffect(() => { setMasHoy([]); setFinHoy(false) }, [fuenteHoy, abierto])

  // LA SIGUIENTE TANDA, AL LLEGAR ABAJO. No se piden todas de golpe a proposito: esta lista es corta
  // en una clinica normal (las citas de hoy y las fichas que ha abierto hoy quien pregunta), pero un
  // dia con la agenda desbordada no puede volcar cientos de filas en el desplegable de la cabecera.
  const cargarMasHoy = () => {
    if (pidiendoHoy || finHoy) return
    const yaHay = vistas.length
    // Todavia no ha llegado la primera tanda: no hay siguiente que pedir, pero TAMPOCO se da la
    // lista por terminada — hacerlo aqui dejaba el desplegable clavado en su primera pagina para
    // siempre si un gesto de la rueda llegaba antes que la respuesta.
    if (!yaHay) return
    // Y si la ultima tanda vino incompleta, es que ya no queda nada: se ahorra la peticion y se
    // deja de preguntar en cada gesto de la rueda.
    if (yaHay % TANDA_HOY !== 0) { setFinHoy(true); return }
    setPidiendoHoy(true)
    api.get(`/pacientes/vistas-hoy?fuente=${fuenteHoy}&limite=${TANDA_HOY}&desde=${yaHay}`)
      .then(r => {
        const tanda = r.data?.pacientes || []
        if (tanda.length) setMasHoy(m => [...m, ...tanda])
        if (!tanda.length || r.data?.hay_mas === false) setFinHoy(true)
        // El freno del servidor se toca en la ULTIMA tanda, no en la primera: es ahi donde la lista
        // se acaba y hasta hoy parecia completa, asi que es ahi donde hay que decirlo.
        setTope(r.data?.recortado ? (r.data.total || null) : null)
      })
      .catch(() => setFinHoy(true))
      .finally(() => setPidiendoHoy(false))
  }

  // Al llegar al final de la lista se pide la siguiente tanda. 48 px de margen para que llegue antes
  // de que se vea el borde y no haya un salto en blanco.
  const alBajarHoy = (e) => {
    const c = e.currentTarget
    if (!c) return
    if (c.scrollTop + c.clientHeight >= c.scrollHeight - 48) cargarMasHoy()
  }
  const resultados = data?.pacientes || []
  const totalResultados = data?.total ?? resultados.length
  const hayMas = totalResultados > resultados.length

  // LAS CITAS DE GENTE QUE TODAVIA NO TIENE FICHA (las primeras visitas). Vienen del mismo
  // endpoint, en un array APARTE — no son pacientes y no pueden mezclarse con ellos: no tienen
  // ficha, ni historial, ni documentos, y `p.id` no existe. Se pintan en su propio bloque, con su
  // rotulo, y llevan a SU CITA en la agenda en vez de a una ficha que no hay.
  //
  // El porque de que esto exista: recepcion buscaba a esa persona, no salia nada, y le creaba la
  // ficha POR SEGUNDA VEZ.
  const sinFicha = data?.sin_ficha || []

  useEffect(() => { setIdx(0) }, [term])

  // Cerrar al pulsar fuera. TAMBIEN CON EL DEDO: en el movil el buscador se pone DELANTE de la
  // cabecera al abrirlo, asi que tiene que poder quitarse tocando la pantalla; `mousedown` llega
  // en la mayoria de los navegadores tactiles pero no siempre (y nunca mientras se arrastra), y
  // quedarse con el buscador pegado encima de la cabecera es quedarse sin menu.
  useEffect(() => {
    const fuera = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setAbierto(false) }
    document.addEventListener('mousedown', fuera)
    document.addEventListener('touchstart', fuera)
    return () => { document.removeEventListener('mousedown', fuera); document.removeEventListener('touchstart', fuera) }
  }, [])

  const abrir = (p) => {
    setAbierto(false); setQ('')
    // La página de Pacientes reabre la ficha desde ?paciente=ID (deep-link ya existente).
    navigate(`/pacientes?paciente=${p.id}`)
  }

  // IR A SU CITA. Una primera visita sin ficha no tiene ficha que abrir: lo único que existe de esa
  // persona es su cita, así que ahí se va. La Agenda ya sabe abrir un día y resaltar una cita
  // concreta con ?fecha=&cita= (deep-link ya existente); no hace falta pantalla nueva.
  const abrirCita = (f) => {
    setAbierto(false); setQ('')
    const p = new URLSearchParams()
    if (f.fecha) p.set('fecha', f.fecha)
    // LA REFERENCIA DE LA AGENDA, no el id propio: la agenda indexa por id_cita_gesden ?? id
    // (utils/citaRef). Con conector, mandar el id propio no resalta nada — o resalta la cita de
    // OTRO paciente si ese numero coincide con su id externo. El servidor ya manda cita_ref.
    p.set('cita', String(f.cita_ref || f.cita_id))
    navigate('/agenda?' + p.toString())
  }
  // B-464 · «CUANDO BUSCAS UN PACIENTE SIN FICHA PERO CON CITA Y PULSAS PARA ENTRAR, DEBERIA LLEVARTE A SU
  // PRE-REGISTRO». Si esa persona ya tiene pre-registro (el servidor manda `intake_id`), se abre ahi,
  // que es donde se le crea la ficha; si no lo tiene, a su cita, como siempre.
  const abrirPreregistro = (f) => {
    setAbierto(false); setQ('')
    navigate('/pre-registros?abrir=' + encodeURIComponent(String(f.intake_id)))
  }

  const onKey = (e) => {
    if (!resultados.length) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => Math.min(i + 1, resultados.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx(i => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); abrir(resultados[idx]) }
    else if (e.key === 'Escape') { setAbierto(false); inputRef.current?.blur() }
  }

  if (!puede) return null

  // ── EN EL MOVIL, AL ABRIRLO, EL BUSCADOR SE PONE DELANTE DE LA CABECERA ─────────────────
  // EL FALLO QUE ARREGLA (el dueño: «el buscador en el movil no veo el desplegable con los
  // pacientes correctamente»). Esta caja vivia SIEMPRE dentro de la cabecera, con `flex` y
  // `minWidth: 0`, o sea con permiso para encogerse hasta cero. Y en un movil no es que se
  // encogiera un poco: midiendo la cabecera de verdad (`components/Layout.jsx`) en una pantalla
  // de 390 px, entre el boton de menu, el de tema, el de chat, modo doctor, modo auxiliar, la
  // campana y el avatar —todos con `flexShrink: 0`— a este buscador le quedan COMO MUCHO 88 px.
  // El desplegable va pegado a los dos lados de la caja (`left: 0; right: 0`), asi que el
  // desplegable medía eso mismo: 88 px, cuando la fila necesita 104 px solo para la foto, los
  // margenes y las pastillas «Cita hoy» / «Vista hoy» —o sea que al NOMBRE del paciente no le
  // quedaba ni un pixel—. No era un desplegable estrecho: era ilegible.
  //
  // Por que ASI y no «un ancho minimo»: un suelo en pixeles dentro de la cabecera no cabe —lo que
  // haria es empujar los botones fuera y sacar la pantalla por la derecha, que es el otro fallo
  // del mismo dia—. Al abrirlo se sale de la fila y se pone ENCIMA, ocupando el ancho de la
  // pantalla; al cerrarlo vuelve a su hueco. Es lo que hace cualquier aplicacion de movil.
  //
  // ── Y LA CAPA TAPA LA CABECERA ENTERA, NO UNA FRANJA ───────────────────────────────────────
  // Iba a `left: 8; right: 8; top: env + 11px` y SIN FONDO: una franja de unos 38 px de alto dentro
  // de una banda de 58, o sea que por arriba, por abajo, por los dos lados y por el hueco
  // transparente entre el campo y la × asomaba lo de detras. Y las insignias de los botones de la
  // cabecera son absolutas y SOBRESALEN de su boton (el contador del chat va en `top: -6`, el punto
  // de «tienes algo que firmar» en `top: -4`), asi que los puntos rojo y ambar quedaban flotando
  // ENCIMA de la barra de buscar. Ahora la capa mide exactamente la banda de la cabecera y lleva
  // fondo: lo de debajo deja de verse porque esta TAPADO.
  //
  // ── EL SAFE-AREA, TAMBIEN A LOS LADOS ──────────────────────────────────────────────────────
  // El relleno horizontal iba en crudo. Con el iPhone TUMBADO la muesca vale 44-59 px A UN LADO: la
  // lupa quedaba debajo del recorte y, segun el lado, tambien la × —que es la unica salida visible
  // de una capa a ancho completo—, asi que el usuario veia el buscador abierto y no encontraba como
  // cerrarlo.
  const expandido = isMobile && abierto
  const estiloCaja = expandido
    // La capa es LA MISMA que la del desplegable (antes, los dos 1200), o sea que el campo sube con
    // ella. t302 · y es la de los desplegables abiertos de la escala de index.css («LAS CAPAS DE LA
    // APLICACIÓN»): dentro de la barra, encima del aviso fijo de una mención (1990), que con 1200 le
    // tapaba los resultados.
    ? { position: 'fixed', left: 0, right: 0, top: 0,
        height: `calc(${ALTO_CABECERA_APP}px + env(safe-area-inset-top, 0px))`, boxSizing: 'border-box',
        background: 'var(--header-bg)', borderBottom: '1px solid var(--border)',
        padding: 'env(safe-area-inset-top, 0px) calc(8px + env(safe-area-inset-right, 0px)) 0'
               + ' calc(8px + env(safe-area-inset-left, 0px))',
        zIndex: 'var(--capa-desplegable-abierto)', display: 'flex', alignItems: 'center', gap: 8 }
    : { position: 'relative', flex: '1 1 220px', maxWidth: '340px', minWidth: 0 }

  // El desplegable (los dos: el de «pacientes de hoy» y el de resultados) cuelga de la caja, asi
  // que con la caja ancha ya sale ancho. Lo que SI cambia en el movil es el ALTO: con el teclado
  // abierto —que es justo cuando se usa— de una pantalla de 667 px quedan poco mas de 300
  // visibles, y un alto fijo de 360 px dejaba la mitad de las filas debajo del teclado.
  const estiloPanel = {
    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 'var(--capa-desplegable-abierto)',
    background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10,
    boxShadow: '0 12px 32px rgba(0,0,0,0.16)', overflow: 'hidden',
    maxHeight: isMobile ? 'min((40vh / var(--z, 1)), 320px)' : 360, overflowY: 'auto',
  }

  return (
    <div ref={boxRef} style={estiloCaja}>
      <InputBusqueda ref={inputRef} value={q}
        onChange={e => { setQ(e.target.value); setAbierto(true) }}
        onFocus={() => setAbierto(true)} onKeyDown={onKey}
        placeholder={isMobile && !expandido ? i18nT('components.BuscadorPacienteGlobal.buscar', 'Buscar') : i18nT('components.BuscadorPacienteGlobal.buscar_paciente', 'Buscar paciente…')}
        contenedorStyle={expandido ? { flex: 1, minWidth: 0 } : undefined}
        lupa lupaSize={15} lupaLeft={10} padLeft={30}
        style={{ width: '100%', padding: '8px 10px', borderRadius: 9,
                 border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)',
                 fontSize: '13px' }} />
      {/* CERRAR, con un boton que se ve. Estando el buscador encima de la cabecera, la unica salida
          era tocar fuera o la tecla Escape —que en un movil no existe—. */}
      {expandido && (
        <button onClick={() => { setAbierto(false); setQ(''); inputRef.current?.blur() }}
          title={i18nT('components.BuscadorPacienteGlobal.cerrar_el_buscador', 'Cerrar el buscador')} aria-label={i18nT('components.BuscadorPacienteGlobal.cerrar_el_buscador', 'Cerrar el buscador')}
          style={{ flexShrink: 0, width: 34, height: 34, display: 'flex', alignItems: 'center',
                   justifyContent: 'center', borderRadius: 9, border: '1px solid var(--border)',
                   background: 'var(--surface)', color: 'var(--text-secondary)', cursor: 'pointer' }}>
          <Ico n="x" s={16} />
        </button>
      )}

      {/* LOS PACIENTES DE HOY, mientras no se ha escrito nada. Dos cosas en UNA SOLA LISTA:
            · los que TIENEN CITA HOY — el que entra por la puerta esta ahi, sin escribir su apellido;
            · las fichas que ESTA persona ya ha abierto hoy, para volver a ellas sin acordarse del
              nombre exacto (con apellidos parecidos eso es abrir tres fichas equivocadas delante del
              paciente).
          No son dos bloques: buscar al mismo paciente en dos sitios es peor que buscarlo escribiendo,
          y quien esta en las dos saldria dos veces. El servidor las funde y cada fila dice de donde
          sale. Las vistas son las de QUIEN pregunta, no las del ordenador: en recepcion el mismo
          equipo lo usan varias personas. */}
      {abierto && term.length < 2 && (
        <div data-desplegable-cabecera="" style={estiloPanel} onScroll={alBajarHoy}>
          {/* LOS DOS BOTONES DE ARRIBA (B-332). Van dentro de UN solo contenedor, no sueltos en el
              panel: las filas de paciente son los botones hijos directos del desplegable y hay una
              guardia que mide la primera de ellas para saber cuanto sitio le queda al nombre
              (routes/buscador-movil-desplegable.test.js). Un boton suelto aqui la haria medir la
              pestaña en vez de una fila. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                        padding: '7px 10px 7px 12px', background: 'var(--surface-2)',
                        borderBottom: '1px solid var(--border-light)' }}>
            <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase',
                           letterSpacing: '.04em', color: 'var(--text-muted)', marginRight: 'auto' }}>
              {i18nT('components.BuscadorPacienteGlobal.pacientes_de_hoy', 'Pacientes de hoy')}
            </span>
            {[['citas', i18nT('components.BuscadorPacienteGlobal.pacientes_hoy', 'Pacientes hoy'), i18nT('components.BuscadorPacienteGlobal.dejar_solo_los_que_tienen', 'Dejar solo los que tienen cita hoy')],
              ['vistas', i18nT('components.BuscadorPacienteGlobal.visto_hoy', 'Visto hoy'), i18nT('components.BuscadorPacienteGlobal.dejar_solo_las_fichas_que', 'Dejar solo las fichas que has abierto hoy')]].map(([clave, rotulo, ayuda]) => {
              const puesto = fuenteHoy === clave
              return (
                <button key={clave} type="button" aria-pressed={puesto}
                  onClick={() => setFuenteHoy(f => (f === clave ? 'todas' : clave))}
                  title={puesto ? i18nT('components.BuscadorPacienteGlobal.quitar_el_filtro_y_ver', 'Quitar el filtro y ver las dos cosas') : ayuda}
                  style={{ padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 800,
                           textTransform: 'uppercase', letterSpacing: '.03em', cursor: 'pointer',
                           border: '1px solid ' + (puesto ? 'var(--primary)' : 'var(--border)'),
                           background: puesto ? 'var(--primary)' : 'var(--surface)',
                           color: puesto ? 'var(--sobre-primary, #fff)' : 'var(--text-secondary)' }}>
                  {rotulo}
                </button>
              )
            })}
          </div>
          {/* SIN NINGUNA, SE DICE. Un desplegable que no pinta nada es indistinguible de uno roto
              (o de una migracion sin aplicar), y quien lo ve deja de usarlo. Y lo que se dice
              depende del boton puesto: con «Visto hoy» puesto, hablar de citas confunde. */}
          {vistas.length === 0 && (
            <div style={{ padding: '12px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              {fuenteHoy === 'citas'
                ? i18nT('components.BuscadorPacienteGlobal.hoy_no_hay_ninguna_cita', 'Hoy no hay ninguna cita. Quita el filtro para ver también las fichas que has abierto.')
                : fuenteHoy === 'vistas'
                  ? i18nT('components.BuscadorPacienteGlobal.todavia_no_has_abierto_ninguna', 'Todavía no has abierto ninguna ficha hoy. Quita el filtro para ver también los pacientes que vienen hoy.')
                  : i18nT('components.BuscadorPacienteGlobal.hoy_no_hay_citas_ni', 'Hoy no hay citas ni fichas abiertas. Aquí saldrán los pacientes que vienen hoy y las fichas que abras, para volver a ellas sin buscar el nombre.')}
            </div>
          )}
          {vistas.map((p, i) => (
            <button key={p.id} onClick={() => abrir(p)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                       padding: '9px 12px', border: 'none', cursor: 'pointer', background: 'transparent',
                       borderTop: i ? '1px solid var(--border-light)' : 'none' }}>
              <FotoPaciente pacienteId={p.id} nombre={p.nombre} apellidos={p.apellidos} size={30} tieneFoto={p.tiene_foto} noQuiereFoto={p.foto_no_deseada === true} mini />
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {[p.nombre, p.apellidos].filter(Boolean).join(' ').trim() || '—'}
                </span>
                {/* LA PROCEDENCIA, EN LA FILA. Quien lee una lista lee la fila, no el rotulo de
                    arriba: con las dos cosas mezcladas, un rotulo comun no dice de donde sale cada
                    una. Y quien esta en las dos lleva las dos etiquetas, en vez de salir dos veces. */}
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
                               fontSize: 11.5, color: 'var(--text-muted)' }}>
                  {p.cita_hoy && (
                    <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.03em',
                                   padding: '1px 6px', borderRadius: 999, background: 'var(--primary-soft, #e8f0fe)',
                                   color: 'var(--primary)', border: '1px solid var(--border-light)' }}>
                      {tx('components.BuscadorPacienteGlobal.cita_hoy', 'Cita hoy{{x}}', { x: p.hora_cita ? ' ' + p.hora_cita : '' })}
                    </span>
                  )}
                  {p.vista_hoy && (
                    <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.03em',
                                   padding: '1px 6px', borderRadius: 999, background: 'var(--surface-2)',
                                   color: 'var(--text-muted)', border: '1px solid var(--border-light)' }}>
                      {tx('components.BuscadorPacienteGlobal.vista_hoy', 'Vista hoy{{x}}', { x: p.hora_vista ? ' ' + p.hora_vista : '' })}
                    </span>
                  )}
                  <span>{[p.num_pac ? 'Nº ' + p.num_pac : null].filter(Boolean).join(' · ')}</span>
                </span>
              </span>
            </button>
          ))}
          {/* Que se vea que hay mas viniendo. Sin esto, bajar del todo parece el final de la lista
              durante el medio segundo que tarda la siguiente tanda. */}
          {pidiendoHoy && (
            <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)',
                          borderTop: '1px solid var(--border-light)' }}>{i18nT('components.BuscadorPacienteGlobal.trayendo_mas', 'Trayendo más…')}</div>
          )}
          {/* Y SI EL SERVIDOR HA LLEGADO A SU TOPE, SE DICE. Con la agenda desbordada quedan
              pacientes detras que esta lista ya no va a servir: callarlo seria dejar creer que eso
              es todo. Se dice cuantos hay y que hacer (escribir el nombre, que va por otra puerta). */}
          {tope && !pidiendoHoy && (
            <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)',
                          borderTop: '1px solid var(--border-light)' }}>
              {tx('components.BuscadorPacienteGlobal.hay_en_total_escribe_el', 'Hay {{tope}} en total: escribe el nombre para encontrar a quien buscas.', { tope })}
            </div>
          )}
        </div>
      )}

      {abierto && term.length >= 2 && (
        <div data-desplegable-cabecera="" style={estiloPanel}>
          {/* «Sin resultados» solo si NO hay ninguna de las dos cosas. Antes bastaba con que no
              hubiera pacientes: con una primera visita encontrada y ningún paciente, el
              desplegable decía «Sin resultados» y debajo pintaba el resultado. */}
          {isFetching && !resultados.length && !sinFicha.length
            ? <div style={{ padding: '12px 14px', fontSize: 12.5, color: 'var(--text-muted)' }}>{i18nT('components.BuscadorPacienteGlobal.buscando', 'Buscando…')}</div>
            : !resultados.length && !sinFicha.length
              ? <div style={{ padding: '12px 14px', fontSize: 12.5, color: 'var(--text-muted)' }}>{i18nT('components.BuscadorPacienteGlobal.sin_resultados', 'Sin resultados.')}</div>
              : resultados.map((p, i) => (
                <button key={p.id} onMouseEnter={() => setIdx(i)} onClick={() => abrir(p)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                           padding: '9px 12px', border: 'none', cursor: 'pointer',
                           background: i === idx ? 'var(--surface-2)' : 'transparent',
                           borderTop: i ? '1px solid var(--border-light)' : 'none' }}>
                  {/* La foto va por FotoPaciente, que la pide con el JWT y la pinta desde un blob. Con
                      <img src="/api/..."> el navegador NO manda el token: el endpoint respondía 401, la
                      imagen fallaba y el onError la escondía, así que el paciente CON foto salía sin
                      nada (y el que no tenía, al menos mostraba su inicial). */}
                  <FotoPaciente pacienteId={p.id} nombre={p.nombre} apellidos={p.apellidos} size={30} tieneFoto={p.tiene_foto} noQuiereFoto={p.foto_no_deseada === true} mini />
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {[p.nombre, p.apellidos].filter(Boolean).join(' ').trim() || '—'}
                    </span>
                    <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {[p.num_pac ? 'Nº ' + p.num_pac : null, p.tel_movil].filter(Boolean).join(' · ') || ''}
                    </span>
                  </span>
                </button>
              ))}
          {/* ── CITAS DE GENTE QUE TODAVÍA NO TIENE FICHA ────────────────────────────────────
              Van DEBAJO de los pacientes y en su propio bloque, con rótulo propio, porque son otra
              cosa: no tienen ficha, ni historial, ni documentos. Mezclarlas con los pacientes es
              justo lo que haría que alguien las tratara como fichas.

              Y se dice lo que son en la propia fila («sin ficha todavía»), no solo en la cabecera:
              quien mira una lista lee la fila, no el rótulo de arriba. */}
          {!!sinFicha.length && (
            <>
              <div style={{ padding: '7px 12px', fontSize: 11, fontWeight: 800, textTransform: 'uppercase',
                            letterSpacing: '.04em', color: 'var(--text-muted)', background: 'var(--surface-2)',
                            borderTop: resultados.length ? '1px solid var(--border)' : 'none' }}>
                {i18nT('components.BuscadorPacienteGlobal.citas_sin_ficha_todavia', 'Citas sin ficha todavía')}
              </div>
              {sinFicha.map((f, i) => (
                <button key={'sf' + f.cita_id} onClick={() => (f.intake_id && puedePre ? abrirPreregistro(f) : abrirCita(f))}
                  title={f.intake_id && puedePre
                    ? i18nT('components.BuscadorPacienteGlobal.esta_persona_todavia_no_tiene', 'Esta persona todavía no tiene ficha: se abre su pre-registro, donde se le crea')
                    : i18nT('components.BuscadorPacienteGlobal.esta_persona_todavia_no_tiene_2', 'Esta persona todavía no tiene ficha: se abre su cita en la agenda')}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
                           padding: '9px 12px', border: 'none', cursor: 'pointer', background: 'transparent',
                           borderTop: i ? '1px solid var(--border-light)' : 'none' }}>
                  {/* Un recuadro con el día en vez de la foto: no hay ficha, así que no hay foto que
                      pedir, y el hueco vacío haría que la fila pareciera un paciente a medio cargar. */}
                  <span style={{ width: 30, height: 30, flexShrink: 0, borderRadius: 8, display: 'flex',
                                 alignItems: 'center', justifyContent: 'center', fontSize: 10.5, fontWeight: 800,
                                 background: 'var(--surface-2)', color: 'var(--text-muted)',
                                 border: '1px dashed var(--border)' }}>
                    {(f.fecha_es || '').slice(0, 5) || '—'}
                  </span>
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.nombre || i18nT('components.BuscadorPacienteGlobal.cita_sin_nombre', 'Cita sin nombre')}
                    </span>
                    <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {[f.intake_id && puedePre ? i18nT('components.BuscadorPacienteGlobal.sin_ficha_tiene_pre_registro', 'Sin ficha · tiene pre-registro') : i18nT('components.BuscadorPacienteGlobal.sin_ficha_todavia', 'Sin ficha todavía'), f.movil, [f.fecha_es, f.hora].filter(Boolean).join(' '),
                        f.citas > 1 ? i18nT('components.BuscadorPacienteGlobal.plural_citas', { one: '{{count}} cita', other: '{{count}} citas' }, { count: f.citas, _forma: 'other' }) : null].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                </button>
              ))}
            </>
          )}

          {/* Si hay más de los que caben, se dice y se ofrece verlos TODOS en /pacientes con la misma
              búsqueda. Sin esto, el buscador parecía "perder" pacientes que sí existen. */}
          {!!resultados.length && hayMas && (
            <button onClick={() => { setAbierto(false); navigate('/pacientes?buscar=' + encodeURIComponent(term)) }}
              style={{ display: 'block', width: '100%', textAlign: 'center', padding: '9px 12px', border: 'none',
                       borderTop: '1px solid var(--border)', background: 'var(--surface-2)', cursor: 'pointer',
                       fontSize: 12.5, fontWeight: 700, color: 'var(--primary)' }}>
              {tx('components.BuscadorPacienteGlobal.ver_los_resultados_en_pacientes', 'Ver los {{totalResultados}} resultados en Pacientes', { totalResultados })}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
